/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  AutocompleteInput,
  DynamicFormInput,
  FormDatePicker,
  FormInput,
  FormSelect,
  FormTextarea,
  FormUpload,
  OpeningHoursEditor,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useEffect, useMemo, useState } from "react";
import { LOG } from "../../utils/logUtil";
import {
  ConceptType,
  HouseholdType,
  IOpeningHoursDay,
  IOpeningHoursException,
  useDropLocations,
} from "../../store/useDropLocations";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import { DropLocationInputItem } from "../../components/form/DynamicFormInput";
import { useMarketPricing } from "../../store/useMarketPricing";
import { Switch } from "@material-tailwind/react";

interface IAddress {
  address?: string;
  city?: string;
  area?: string;
  type?: string;
  postalCode?: string;
  lat?: string;
  long?: string;
  conceptType?: ConceptType;
  openingDate?: Date;
  highBuildingsRatio?: number;
  inhabitantsPerHousehold?: number;
  averageIncomeInNeighbourhood?: number;
  householdsType?: HouseholdType;
  rentPerYear?: number;
  squareMetersPerStore?: number;
  lockerSpaceUsed?: number;
}

// Google often omits `sublocality` from geocoding results (smaller towns and
// villages). Walk a priority list of address-component types so the Area
// field still resolves to a sensible value instead of coming back empty.
const AREA_COMPONENT_TYPES = [
  "sublocality",
  "neighborhood",
  "postal_town",
  "administrative_area_level_2",
  "locality",
];
const pickArea = (
  components: { types: string[]; long_name: string }[]
): string | undefined => {
  for (const type of AREA_COMPONENT_TYPES) {
    const match = components.find((c) => c.types.includes(type));
    if (match?.long_name) return match.long_name;
  }
  return undefined;
};

function AddDropLocationScreen() {
  /** Hooks */
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const { addedDropLocation, clearAddedDropLocation, addDropLocation } =
    useDropLocations();
  const { uploadImage, clearUploadedImage, uploadedImage } = useUploadImage();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  /** State */
  const [submitted, setSubmitted] = useState(false);
  const [value, setValue] = useState<{ value: { place_id: string } } | null>(
    null
  );
  const [name, setName] = useState<string | null>(null);
  const [cost_place, setCostPlace] = useState<string | null>(null);
  const [address, setAddress] = useState<IAddress | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [openingHours, setOpeningHours] = useState<IOpeningHoursDay[]>([]);
  const [openingHoursExceptions, setOpeningHoursExceptions] = useState<
    IOpeningHoursException[]
  >([]);
  const [openingHoursNote, setOpeningHoursNote] = useState<{
    en?: string;
    nl?: string;
  }>({});
  const [description, setDescription] = useState<string | null>(null);
  const [acceptedInputs, setAcceptedInputs] = useState<DropLocationInputItem[]>(
    []
  );
  const [notAcceptedInputs, setNotAcceptedInputs] = useState<
    DropLocationInputItem[]
  >([]);
  const [conceptType, setConceptType] = useState<ConceptType | null>(null);
  const [highBuildingsRatio, setHighBuildingsRatio] = useState<string>("");
  const [inhabitantsPerHousehold, setInhabitantsPerNeighbourhood] =
    useState<string>("");
  const [averageIncomeInNeighbourhood, setAverageIncomeInNeighbourhood] =
    useState<string>("");
  const [householdsType, setHouseholdsType] = useState<HouseholdType | null>(
    null
  );
  const [openingDate, setOpeningDate] = useState<Date | null>(null);
  const [rentPerYear, setRentPerYear] = useState<string>("");
  const [squareMetersPerStore, setSquareMetersPerStore] = useState<string>("");
  const [lockerSpaceUsed, setLockerSpaceUsed] = useState<string>("");
  const [isPost, setIsPost] = useState<boolean>(false);
  const [showInApp, setShowInApp] = useState<boolean>(true);
  const [hasBrinksMachines, setHasBrinksMachines] = useState<boolean>(false);

  const fetchPlace = async (placeId: string) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_API}`
      );
      const jsonResponse = await response.json();
      if (jsonResponse?.results?.length) {
        const result = jsonResponse.results[0];
        const address = {
          address: result.formatted_address,
          city: result.address_components.find((c: { types: string[] }) =>
            c.types.includes("locality")
          )?.long_name,
          area: pickArea(result.address_components),
          postalCode: result.address_components.find((c: { types: string[] }) =>
            c.types.includes("postal_code")
          )?.long_name,
          lat: result.geometry.location.lat,
          long: result.geometry.location.lng,
        };
        setAddress(address);
      }
    } catch (error) {
      LOG.error("Error on fetching place", { error });
    }
  };

  useEffect(() => {
    getAllMarketPrices();
    return () => {
      clearUploadedImage();
    };
  }, []);

  useEffect(() => {
    if (addedDropLocation) {
      clearAddedDropLocation();
      navigate(-1);
    }
  }, [addedDropLocation]);

  useEffect(() => {
    if (value) {
      fetchPlace(value?.value?.place_id);
    }
  }, [value]);

  const onSubmit = () => {
    if (!name) {
      setAlert({
        isOpen: true,
        message: "Please enter a name.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!cost_place) {
      setAlert({
        isOpen: true,
        message: "Please enter a cost place.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!address) {
      setAlert({
        isOpen: true,
        message: "Please try another address.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!address?.area) {
      setAlert({
        isOpen: true,
        message: "Please try another address.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!image) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    let body = {
      ...address,
      name,
      cost_place,
      image: uploadedImage,
      openingHours,
      openingHoursExceptions,
      openingHoursNote,
      description: description ?? "",
      notAccepting:
        acceptedInputs?.length > 1
          ? notAcceptedInputs
            .filter((input) => input.value !== "")
            ?.map((input) => ({ name: input.value }))
          : [],
      accepting:
        acceptedInputs?.length > 1
          ? acceptedInputs
            .filter((input) => input.value !== "")
            ?.map((input) => ({ name: input.value }))
          : [],
      isPost,
      showInApp,
      hasBrinksMachines,
    };
    if (conceptType) body.conceptType = conceptType;
    if (highBuildingsRatio)
      body.highBuildingsRatio = Number(highBuildingsRatio);
    if (inhabitantsPerHousehold)
      body.inhabitantsPerHousehold = Number(inhabitantsPerHousehold);
    if (averageIncomeInNeighbourhood)
      body.averageIncomeInNeighbourhood = Number(averageIncomeInNeighbourhood);
    if (householdsType) body.householdsType = householdsType;
    if (openingDate) body.openingDate = openingDate;
    if (rentPerYear) body.rentPerYear = Number(rentPerYear);
    if (squareMetersPerStore) body.squareMetersPerStore = Number(squareMetersPerStore);
    if (lockerSpaceUsed) body.lockerSpaceUsed = Number(lockerSpaceUsed);
    addDropLocation(body);
    LOG.info("Address", { address });
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const selectConceptType = (e: any) => setConceptType(e?.target?.value);
  const selectHouseholdType = (e: any) => setHouseholdsType(e?.target?.value);

  const goBack = () => navigate(-1);
  const imageError = submitted && !image;

  const marketPricesToSelect = useMemo(() => {
    let marketPricesToSelect = [];
    for (let mp of marketPrices) {
      if (
        !acceptedInputs?.some((input) => input.value === mp.name) &&
        !notAcceptedInputs?.some((input) => input.value === mp.name)
      ) {
        marketPricesToSelect.push({
          value: mp._id ?? "",
          label: mp.name,
        });
      }
    }
    return marketPricesToSelect;
  }, [acceptedInputs, notAcceptedInputs, marketPrices]);

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new drop location
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Drop location
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <FormInput
              placeholder="Type name here..."
              label="Name"
              error={submitted && !name}
              value={name ?? ""}
              onChange={(e) => setName(e.target.value)}
            />
            <FormInput
              placeholder="Type cost place here..."
              label="Cost Place"
              error={submitted && !cost_place}
              value={cost_place ?? ""}
              onChange={(e) => setCostPlace(e.target.value)}
            />
            <Switch
              crossOrigin={"anonymous"}
              color="teal"
              ripple
              label="PostNL Service"
              checked={isPost}
              onChange={(e) => setIsPost(e?.target.checked)}
            />
            <AutocompleteInput
              value={value}
              onChange={setValue}
              label="Select drop place"
            />
            <FormInput
              placeholder="Address will be filled here..."
              label="Address"
              disabled
              error={submitted && !address?.address}
              value={address?.address ?? ""}
            />
            <FormInput
              placeholder="City will be filled here..."
              label="City"
              disabled
              error={submitted && !address?.city}
              value={address?.city ?? ""}
            />
            <FormSelect
              label="Select Location concept type"
              placeholder="Select concept type"
              value={conceptType ?? ""}
              // @ts-ignore
              options={
                Object.entries(ConceptType).map(([key, value]) => ({
                  value: value,
                  label: key,
                })) ?? []
              }
              // error={submitted && !conceptType}
              onChange={selectConceptType}
            />
            <FormDatePicker
              label="Store Opening date"
              placeholder="Select date"
              value={openingDate ?? new Date()}
              onChange={setOpeningDate}
              labelClassName="mt-4"
            />
            <FormInput
              placeholder="High buildings ratio (Hoogbouw ratio / stedelijkheidsklasse)"
              label="High buildings ratio"
              type="number"
              onChange={(e) => setHighBuildingsRatio(e.target.value)}
              // error={!!highBuildingsRatio && isNaN(highBuildingsRatio)}
              value={highBuildingsRatio ?? "}"}
            />
            <FormInput
              placeholder="Inhabitants per household"
              label="Inhabitants per household"
              type="number"
              onChange={(e) => setInhabitantsPerNeighbourhood(e.target.value)}
              // error={submitted && !inhabitantsPerHousehold}
              value={inhabitantsPerHousehold ?? ""}
            />
            <FormInput
              placeholder="Average income in neighbourhood"
              label="Average income in neighbourhood"
              type="number"
              onChange={(e) => setAverageIncomeInNeighbourhood(e.target.value)}
              // error={submitted && !averageIncomeInNeighbourhood}
              value={averageIncomeInNeighbourhood ?? ""}
            />
            <FormSelect
              label="Select household type"
              placeholder="Select household type"
              value={householdsType ?? ""}
              // @ts-ignore
              options={
                Object.entries(HouseholdType).map(([key, value]) => ({
                  value: value,
                  label: key,
                })) ?? []
              }
              // error={submitted && !householdsType}
              onChange={selectHouseholdType}
            />
            <FormInput
              placeholder="Area will be filled here..."
              label="Area"
              disabled
              error={submitted && !address?.area}
              value={address?.area ?? ""}
            />
            <FormInput
              placeholder="Postal code will be filled here..."
              label="Postal code"
              disabled
              error={submitted && !address?.postalCode}
              value={address?.postalCode ?? ""}
            />
            <FormInput
              placeholder="Latitude will be filled here..."
              label="Latitude"
              disabled
              error={submitted && !address?.lat}
              value={address?.lat ?? ""}
            />
            <FormInput
              placeholder="Longitude will be filled here..."
              label="Longitude"
              disabled
              error={submitted && !address?.long}
              value={address?.long ?? ""}
            />
            <OpeningHoursEditor
              label="Opening hours"
              value={openingHours}
              onChange={setOpeningHours}
              exceptions={openingHoursExceptions}
              onExceptionsChange={setOpeningHoursExceptions}
              note={openingHoursNote}
              onNoteChange={setOpeningHoursNote}
            />
            <FormTextarea
              placeholder="Type a short description shown in the mobile app..."
              label="Location description"
              value={description ?? ""}
              onChange={(e) => setDescription(e.target.value)}
            />
            <FormInput
              placeholder="Rent per year"
              label="Rent per year"
              type="number"
              onChange={(e) => setRentPerYear(e.target.value)}
            />
            <FormInput
              placeholder="Square meters per store"
              label="Square meters per store"
              type="number"
              onChange={(e) => setSquareMetersPerStore(e.target.value)}
            />
            <FormInput
              placeholder="Locker space used"
              label="Locker space used"
              type="number"
              onChange={(e) => setLockerSpaceUsed(e.target.value)}
            />
            <DynamicFormInput
              label="Accepted items"
              inputs={acceptedInputs}
              setInputs={setAcceptedInputs}
              options={marketPricesToSelect ?? []}
            />
            <DynamicFormInput
              label="Not accepted items"
              inputs={notAcceptedInputs}
              setInputs={setNotAcceptedInputs}
              options={marketPricesToSelect ?? []}
            />
            <Switch
              crossOrigin={"anonymous"}
              color="teal"
              ripple
              label="Show this location in app"
              checked={showInApp}
              onChange={(e) => setShowInApp(e?.target.checked)}
            />
            <div className="mt-4">
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label="Has Brinks Machines"
                checked={hasBrinksMachines}
                onChange={(e) => setHasBrinksMachines(e?.target.checked)}
              />
            </div>
            {/* Informational only — no switch here on purpose. A store that
                is still being filled in must not be able to land in the
                production intake list by accident; enabling is a separate,
                deliberate act on the Edit page once carriers are ready. */}
            <div className="mt-6 border-t border-gray-border pt-6">
              <p className="font-satoshi font-medium text-base text-dark mb-2">
                Parcel handoffs
              </p>
              <p className="text-xs text-gray-600">
                Parcel handoffs are disabled by default. Save the location,
                then enable them from the Edit Location page after carrier
                setup is ready.
              </p>
            </div>
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton onClick={goBack} label="Cancel" />
              <PrimaryButton onClick={onSubmit} className="ml-4" label="Save" />
            </div>
          </div>
        </div>
        <div
          className={`w-full sm:w-1/3 sm:ml-8 bg-white border border-gray-border drop-shadow-md h-[360px]`}
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Drop location icon
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <FormUpload
              onChange={onImageChange}
              value={image}
              error={imageError}
              accept=".png, .jpg, .jpeg, .webp"
              formatsHint="PNG, JPG or WebP"
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton onClick={goBack} label="Cancel" />
              <PrimaryButton
                onClick={onUploadImage}
                className="ml-4"
                label="Save"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddDropLocationScreen);
